import { useState, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';

const { WLC_TRACK_PROGRESS_CONTENT_ID } = process.env;

/**
 * useWLCData – Manages WLC user-state lifecycle.
 *
 * Responsibilities:
 *  • Load persisted user data (tours, getting-started flag) on mount.
 *  • Persist changes when toursCompleted or gettingStartedCompleted change.
 *  • Expose updateTourStatus callback for child components.
 *
 * @param {Object}   params
 * @param {Function} params.getUserData    – Redux action (user.get)
 * @param {Function} params.postUserData   – Redux action (user.post)
 * @param {Function} params.refreshToken   – Redux action
 * @param {Function} params.handleNav      – Navigation callback (used for getting-started redirect)
 *
 * @returns {{ toursCompleted, loadingState, gettingStartedCompleted, updateTourStatus }}
 */
const useWLCData = ({ getUserData, postUserData, refreshToken, handleNav }) => {
  const [toursCompleted, setToursCompleted] = useState({});
  const [isOnboard, setIsOnboard] = useState(false);
  const [isLegacyMember, setIsLegacyMember] = useState(false);
  const [gettingStartedCompleted, setGettingStartedCompleted] = useState(false);
  const [wlcProgress, setWlcProgress] = useState(null);
  const [loadingState, setLoadingState] = useState(true);

  // Track previous values so we only persist on actual changes
  const [prevTours, setPrevTours] = useState({});
  const [prevGettingStarted, setPrevGettingStarted] = useState(false);
  const [prevWlcProgress, setPrevWlcProgress] = useState(null);
  const [prevIsOnboard, setPrevIsOnboard] = useState(false);

  // ── Load persisted state on mount ─────────────────────────────────
  useEffect(() => {
    const loadState = async () => {
      try {
        const savedState = await getUserData(WLC_TRACK_PROGRESS_CONTENT_ID);
        console.log('useWLCData', 'savedState', savedState, savedState?.isOnboard || savedState?.isLegacyMember);
        const tours = savedState?.toursCompleted || {};
        const isOnboard = savedState?.isOnboard || savedState?.isLegacyMember || false;
        const isLegacyMember = savedState?.isLegacyMember || false;
        const gsCompleted = savedState?.gettingStartedCompleted || false;
        const progress = savedState?.wlcProgress || null;

        setToursCompleted(tours);
        setIsOnboard(isOnboard);
        setIsLegacyMember(isLegacyMember);
        setGettingStartedCompleted(gsCompleted);
        setWlcProgress(progress);
        setPrevTours(tours);
        setPrevIsOnboard(isOnboard);
        setPrevGettingStarted(gsCompleted);
        setPrevWlcProgress(progress);
        setLoadingState(false);

        // Redirect to getting-started if user hasn't completed it
        /*if (!gsCompleted && !window.PREVENT_NAV_TO_GETTING_STARTED) {
          setGettingStartedCompleted(true);
          handleNav(null, null, 'getting-started');
        }
        window.PREVENT_NAV_TO_GETTING_STARTED = null;*/
      } catch (err) {
        console.error('Error loading WLC state:', err);
        setLoadingState(false);
      }
    };

    loadState();
    refreshToken();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist changes ───────────────────────────────────────────────
  useEffect(() => {
    if (loadingState) return;

    const toursChanged = !isEqual(prevTours, toursCompleted);
    const gsChanged = prevGettingStarted !== gettingStartedCompleted;
    const wlcProgressChanged = !isEqual(prevWlcProgress, wlcProgress);
    const isOnboardChanged = !isEqual(prevIsOnboard, isOnboard);

    if (!toursChanged && !gsChanged && !wlcProgressChanged && !isOnboardChanged) return;

    const persistState = async () => {
      try {
        const currentData = await getUserData(WLC_TRACK_PROGRESS_CONTENT_ID);
        await postUserData(WLC_TRACK_PROGRESS_CONTENT_ID, {
          ...currentData,
          toursCompleted,
          isOnboard,
          gettingStartedCompleted,
          wlcProgress
        });
        setPrevTours(toursCompleted);
        setPrevIsOnboard(isOnboard);
        setPrevGettingStarted(gettingStartedCompleted);
        setPrevWlcProgress(wlcProgress);
      } catch (err) {
        console.error('Error persisting WLC state:', err);
      }
    };

    persistState();
  }, [toursCompleted, isOnboard, gettingStartedCompleted, wlcProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Public callbacks ──────────────────────────────────────────────
  const updateTourStatus = useCallback((tour, status) => {
    setToursCompleted(prev => ({ ...prev, [tour]: status }));
  }, []);

  const updateWlcProgress = useCallback(progress => {
    setWlcProgress(prev => ({ ...prev, ...progress }));
  }, []);

  const completeOnboarding = useCallback(() => {
    setIsOnboard(true);
  }, []);

  return {
    toursCompleted,
    isLegacyMember,
    isOnboard,
    loadingState,
    gettingStartedCompleted,
    wlcProgress,
    completeOnboarding,
    updateTourStatus,
    updateWlcProgress
  };
};

export default useWLCData;
